import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ChevronRight, Calendar, Tag, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ResourceDetail = () => {
  const { slug } = useParams();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchResource();
  }, [slug]);

  const fetchResource = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/resources/${slug}`);
      
      if (!response.ok) {
        throw new Error('Resource not found');
      }

      const data = await response.json();
      setResource(data);
    } catch (err) {
      console.error('Error fetching resource:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const typeLabels = {
    'blog': 'Blog',
    'case-study': 'Case Study',
    'rnd': 'R&D',
    'soft-paper': 'Soft Paper',
    'pdf': 'PDF'
  };

  if (loading) {
    return (
      <div className="bg-gray-50 text-gray-900 min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="ml-3 text-gray-600">Loading resource...</span>
        </div>
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="bg-gray-50 text-gray-900 min-h-screen">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-40 px-6">
          <h1 className="text-4xl font-bold mb-4">Resource Not Found</h1>
          <p className="text-gray-600 mb-8">The resource you're looking for doesn't exist or has been removed.</p>
          <Link 
            to="/resources" 
            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
          >
            Back to Resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 text-gray-900 min-h-screen">
      <Navbar />

      {/* Hero Section with Cover Image */}
      <section className="pt-32 pb-12 px-6 lg:px-12 relative">
        {/* Cover Image Background */}
        {resource.coverImageUrl && (
          <div className="absolute inset-0 overflow-hidden">
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${resource.coverImageUrl})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black"></div>
          </div>
        )}

        <div className="max-w-4xl mx-auto relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center text-sm text-gray-600 mb-8">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/resources" className="hover:text-white transition-colors">Resources</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-400">{typeLabels[resource.type]}</span>
          </div>

          {/* Type Badge */}
          <div className="mb-6">
            <span className="px-4 py-2 rounded-full text-sm font-medium bg-blue-500/20 text-blue-500 border border-blue-500/30">
              {typeLabels[resource.type]}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            {resource.title}
          </h1>

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-6">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              <span>{format(new Date(resource.createdAt), 'MMMM dd, yyyy')}</span>
            </div>
            {resource.tags && resource.tags.length > 0 && (
              <div className="flex items-center">
                <Tag className="w-4 h-4 mr-2" />
                <span>{resource.tags.length} {resource.tags.length === 1 ? 'tag' : 'tags'}</span>
              </div>
            )}
          </div>

          {/* Tags */}
          {resource.tags && resource.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {resource.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 rounded-md text-sm bg-gray-50 text-gray-700 border border-gray-3000"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Short Description */}
          <p className="text-xl text-gray-700 leading-relaxed">
            {resource.shortDescription}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {resource.source === 'internal' && resource.content && (
            <div 
              className="prose prose-invert prose-lg max-w-none
                prose-headings:text-white prose-headings:font-bold
                prose-p:text-gray-700 prose-p:leading-relaxed
                prose-a:text-blue-500 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-white prose-strong:font-semibold
                prose-ul:text-gray-700 prose-ol:text-gray-700
                prose-li:text-gray-700
                prose-blockquote:border-l-blue-500 prose-blockquote:text-gray-600
                prose-code:text-blue-500 prose-code:bg-white prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                prose-pre:bg-white prose-pre:border prose-pre:border-gray-30000"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {resource.content}
              </ReactMarkdown>
            </div>
          )}

          {resource.source === 'external-url' && resource.externalUrl && (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-6">This resource is hosted externally on Medium.</p>
              <a
                href={resource.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-4 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors text-lg font-medium"
              >
                Read on Medium →
              </a>
            </div>
          )}

          {resource.source === 'pdf' && resource.pdfUrl && (
            <div className="w-full">
              <div className="mb-4 p-4 bg-white border border-gray-30000 rounded-lg">
                <p className="text-gray-600 text-sm">
                  PDF Document • <a href={resource.pdfUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Open in new tab</a>
                </p>
              </div>
              <div className="w-full bg-white border border-gray-30000 rounded-lg overflow-hidden" style={{ height: '800px' }}>
                <iframe
                  src={`${resource.pdfUrl}#view=FitH`}
                  title={resource.title}
                  className="w-full h-full"
                  style={{ border: 'none' }}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Back to Resources */}
      <section className="py-12 px-6 lg:px-12 border-t border-gray-30000">
        <div className="max-w-4xl mx-auto text-center">
          <Link 
            to="/resources" 
            className="inline-block px-6 py-3 bg-white hover:bg-gray-50 border border-gray-3000 rounded-lg transition-colors"
          >
            ← Back to All Resources
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResourceDetail;
